/**
 * RecommendedTab Component
 * Displays personalized trip recommendations using collaborative filtering
 */
import React, { useEffect, useState } from 'react';
import { Row, Col, Spinner } from 'react-bootstrap';
import { Badge } from 'react-bootstrap';
import { discoveryService } from '../../../services/api/discoveryService';
import { TripRecommendation } from '../../../types/discovery.types';
import { DiscoveryTripCard } from '../DiscoveryTripCard';
import './DiscoveryTab.css';

export const RecommendedTab: React.FC = () => {
  const [recommendations, setRecommendations] = useState<TripRecommendation[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await discoveryService.getRecommendations({ limit: 12 });
      setRecommendations(data.recommendations);
    } catch (err) {
      setError('Failed to load recommendations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className='discovery-tab-loading'>
        <Spinner
          animation='border'
          variant='primary'
        />
        <p>Finding trips you'll love...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='discovery-tab-error'>
        <p>{error}</p>
        <button
          className='btn btn-primary'
          onClick={loadRecommendations}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (recommendations.length === 0) {
    return (
      <div className='discovery-tab-empty'>
        <h3>No recommendations yet</h3>
        <p>
          Start exploring and liking trips to get personalized recommendations!
        </p>
      </div>
    );
  }

  return (
    <div className='discovery-tab'>
      <div className='discovery-tab-header'>
        <h2>Recommended for You</h2>
        <p>Based on your interests and similar travelers</p>
      </div>

      <Row
        xs={1}
        md={2}
        lg={3}
        className='g-4'
      >
        {recommendations.map((recommendation) => (
          <Col key={recommendation.trip._id}>
            <DiscoveryTripCard
              trip={recommendation.trip}
              score={recommendation.score}
              reason={recommendation.reason}
              badge={
                recommendation.supportingUsers &&
                recommendation.supportingUsers > 5 ? (
                  <Badge bg='success'>
                    {recommendation.supportingUsers}+ travelers love this
                  </Badge>
                ) : null
              }
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};
